// Runtime half of the version check (production builds only, see index.ts).
//
// Polls /version.json on window focus, on visibilitychange, every 5 minutes
// and once at start-up; hands the result to decideUpdateAction() and either
// reloads or shows the "New version available" toast (components/AppUpdateWatcher.tsx
// renders it from the store below). A pending update is applied on the next
// client-side navigation as a full page load of the new URL.
//
// "Dirty" is deliberately simple: any `input`/`change` event inside a form
// field (input/textarea/select/contenteditable) since the last navigation or
// form submit. It errs on the side of never auto-reloading over typed text.
import { decideUpdateAction } from "./decideUpdateAction";
import { getCurrentBuildId, safeSessionStorage } from "./browser";

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const VERSION_JSON_PATH = "/version.json";
/** sessionStorage key set right before we reload for a given buildId. */
export const RELOADED_FOR_KEY_PREFIX = "administr:reloaded-for:";

export interface UpdateToastState {
  /** buildId the toast is shown for, or null when hidden. */
  toastBuildId: string | null;
}

let toastState: UpdateToastState = { toastBuildId: null };
const listeners = new Set<() => void>();
const toastShownFor = new Set<string>();
let pendingBuildId: string | null = null;
let currentBuildId = "";
let dirty = false;
let lastActivityAt = 0;
let started = false;

function emit() {
  listeners.forEach((l) => l());
}

export function subscribeUpdateToast(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUpdateToastState(): UpdateToastState {
  return toastState;
}

function reloadedFor(buildId: string): boolean {
  try {
    return !!safeSessionStorage()?.getItem(RELOADED_FOR_KEY_PREFIX + buildId);
  } catch {
    return false;
  }
}

function reloadTo(buildId: string, url?: string) {
  try {
    safeSessionStorage()?.setItem(RELOADED_FOR_KEY_PREFIX + buildId, String(Date.now()));
  } catch {
    // storage unavailable: still reload; decide() will see alreadyReloadedFor=false
  }
  if (url) window.location.assign(url);
  else window.location.reload();
}

function showToast(buildId: string) {
  if (toastShownFor.has(buildId)) return; // once per buildId
  toastShownFor.add(buildId);
  toastState = { toastBuildId: buildId };
  emit();
}

export function dismissUpdateToast(): void {
  if (!toastState.toastBuildId) return;
  toastState = { toastBuildId: null };
  emit();
}

/** "Reload" action of the toast. */
export function reloadForUpdate(): void {
  const id = toastState.toastBuildId ?? pendingBuildId;
  if (id) reloadTo(id);
}

function applyDecision(serverBuildId: string, navigating: boolean, url?: string) {
  const action = decideUpdateAction({
    currentBuildId,
    serverBuildId,
    hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
    idleMs: Date.now() - lastActivityAt,
    dirty,
    navigating,
    alreadyReloadedFor: reloadedFor(serverBuildId),
  });
  if (action === "none") return;
  pendingBuildId = serverBuildId; // remembered: the next navigation reloads
  if (action === "reload") reloadTo(serverBuildId, url);
  else showToast(serverBuildId);
}

/** Fetch /version.json and act on it. Network/parse errors are ignored. */
export async function checkForUpdate(): Promise<void> {
  if (!currentBuildId) return;
  let serverBuildId: string | null = null;
  try {
    const res = await fetch(`${VERSION_JSON_PATH}?ts=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;
    const data: unknown = await res.json();
    const id = (data as { buildId?: unknown } | null)?.buildId;
    serverBuildId = typeof id === "string" ? id : null;
  } catch {
    return;
  }
  if (serverBuildId) applyDecision(serverBuildId, false);
}

/** Called by the React side on every wouter location change (not on mount). */
export function onClientNavigation(): void {
  dirty = false;
  if (pendingBuildId) applyDecision(pendingBuildId, true, window.location.href);
}

function isFormField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export function startUpdateWatcher(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  currentBuildId = getCurrentBuildId();
  lastActivityAt = Date.now();

  const markDirty = (e: Event) => {
    if (isFormField(e.target)) dirty = true;
  };
  document.addEventListener("input", markDirty, true);
  document.addEventListener("change", markDirty, true);
  document.addEventListener("submit", () => { dirty = false; }, true);

  const activity = () => { lastActivityAt = Date.now(); };
  for (const type of ["pointerdown", "keydown", "touchstart", "wheel"] as const) {
    window.addEventListener(type, activity, { capture: true, passive: true });
  }

  const check = () => { void checkForUpdate(); };
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", check);
  window.setInterval(check, VERSION_CHECK_INTERVAL_MS);
  check();
}
